import * as React from 'react';
import { Label as LabelPrimitive } from 'radix-ui';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface LabelProps extends React.ComponentProps<typeof LabelPrimitive.Root> {
    readonly required?: boolean;
    /** Renders an info icon with a hover/focus tooltip after the label text. */
    readonly tooltip?: React.ReactNode;
}

function Label({ className, children, required, tooltip, ...props }: LabelProps) {
    return (
        <LabelPrimitive.Root
            data-slot="label"
            className={cn(
                'flex items-center gap-1 text-sm leading-none font-medium text-text-primary select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 dark:text-text-secondary',
                className,
            )}
            {...props}
        >
            {children}
            {required && (
                <span aria-hidden="true" className="text-error-600 dark:text-error-400">
                    *
                </span>
            )}
            {tooltip && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        {/* stopPropagation: this label may have `htmlFor` pointing at a
                            Switch/Checkbox — without it, clicking the icon would bubble to
                            the native <label> and toggle that control instead of just
                            opening the tooltip. */}
                        <Info
                            className="size-3.5 shrink-0 cursor-help text-text-tertiary"
                            onClick={(event) => event.stopPropagation()}
                        />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px]">
                        <p className="text-xs">{tooltip}</p>
                    </TooltipContent>
                </Tooltip>
            )}
        </LabelPrimitive.Root>
    );
}

export { Label };
